import { z } from 'zod'

export const relativeDateTemplateSchema = z.union([
  z.object({ $monthsAgo: z.number().int().min(0) }).strict(),
  z.object({ $daysAhead: z.number().int() }).strict(),
  // Resolves to a calendar year, not a date — for year-valued facets.
  z.object({ $yearsAgo: z.number().int().min(0) }).strict(),
])

export const filterConditionSchema = z.object({
  field: z.enum([
    'customerType',
    'customerStatus',
    'propertyType',
    'constructionYear',
    'servicePackageId',
    'visitDateFrom',
    'visitDateTo',
    'maintenanceTaskId',
    'opportunityStage',
    'city',
    // Prefix match: '00' covers a whole region, '00320' a single code.
    'postalCode',
    'userRole',
    'relationshipType',
    'customerTagId',
    'accountManagerUserId',
    'visitDate',
    // Aggregate facet fields — scalar comparisons against MAX/MIN of the
    // customer's nuohous visits. See audienceFacets.ts for the task-id list.
    'lastNuohousDate',
    'nextNuohousDate',
    // Followup fields. receivedNewsletterId: users who were successfully sent
    // one of the given newsletters (durable notifications log, no drift).
    // usedDiscountCodeId: customers whose visits redeemed one of the codes.
    'receivedNewsletterId',
    'usedDiscountCodeId',
    // Purchased calling lists. Person-path facets, so they match a phone-only
    // lead with no customer, property or opportunity to join through.
    // callingListBatch: provenance — one delivery, used to exclude a bad one.
    // callingListArea: postal-code prefix, how a campaign is scoped to a city.
    // fromCallingList: on any purchased list at all — the durable "every cold
    // lead we hold" campaign, which keeps growing as new batches are imported.
    'fromCallingList',
    'callingListBatch',
    'callingListArea',
    // One slice of the audience, written "<slice>/<slices>" ('1/3'). Splits an
    // audience across several sends — e.g. the same letter signed by three
    // salespeople. Hashed from the user id, so slices never overlap and no
    // send has to wait for another.
    'splitBucket',
  ]),
  operator: z.enum(['eq', 'in', 'gte', 'lte']),
  value: z.union([
    z.string(),
    z.number(),
    z.array(z.string()),
    z.array(z.number()),
    relativeDateTemplateSchema,
  ]),
})

export const filterGroupSchema = z.object({
  conditions: z.array(filterConditionSchema),
})

export const audienceFilterSchema = z.object({
  include: z.array(filterGroupSchema),
  exclude: z.array(filterGroupSchema),
  includeEmails: z.array(z.string().email()).optional(),
  excludeEmails: z.array(z.string().email()).optional(),
})

export type RelativeDateTemplate = z.infer<typeof relativeDateTemplateSchema>
export type FilterCondition = z.infer<typeof filterConditionSchema>
export type FilterGroup = z.infer<typeof filterGroupSchema>
export type AudienceFilter = z.infer<typeof audienceFilterSchema>
